import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	Box,
	Checkbox,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
	Typography
} from "@mui/material";
import ActionCell from "components/personnelAndFleet/common/ActionCell";
import StatusCell from "components/personnelAndFleet/common/StatusCell";
import { FLEET_STATUS_LABELS, FleetVehicleStatus } from "enums/FleetEnums";
import { FleetVehicle } from "models/FleetVehicle";
import { useMemo, useState } from "react";
import { useColumnVisibility } from "hooks/useColumnVisibility";
import ColumnVisibilityPopover, { ColumnDef } from "components/ColumnVisibilityPopover";
import { HeadCell, Order, cellStyle, getComparator, useRangeSelection } from "../common/tableUtils";
import { getFleetVehicleActionItems } from "./fleetUtils";

const checkboxColumnWidth = 48;
const actionColumnWidth = 40;

const headCells: (HeadCell<FleetVehicle> & { alwaysVisible?: boolean })[] = [
	{ id: "registration", label: "FLEET NUMBER", width: 13, alwaysVisible: true },
	{ id: "type", label: "VEHICLE TYPE", width: 15 },
	{ id: "lengthM", label: "LENGTH (M)", width: 9 },
	{ id: "refrigerated", label: "REFRIGERATED", width: 10 },
	{ id: "defaultDriver", label: "DEFAULT DRIVER", width: 13 },
	{ id: "tractorRego", label: "REG. TRUCK/TRACTOR", width: 13 },
	{ id: "trailer1Rego", label: "REG. TRAILER 1", width: 13 },
	{ id: "trailer2Rego", label: "REG. TRAILER 2", width: 13 },
	{ id: "status", label: "STATUS", width: 8 }
];

function getRefrigeratedLabel(value: string | boolean) {
	if (typeof value === "boolean") {
		return value ? "Yes" : "No";
	}

	if (String(value).toLowerCase() === "yes") {
		return "Yes";
	}

	if (String(value).toLowerCase() === "no") {
		return "No";
	}

	return "-";
}

type FleetDataTableProps = {
	rows: FleetVehicle[];
	selectedRows: Set<number>;
	setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
	selectionDisabled?: boolean;
	onEditVehicle: (vehicle: FleetVehicle) => void;
	onDuplicateVehicle: (vehicle: FleetVehicle) => void;
	onDeactivateVehicle: (vehicle: FleetVehicle) => void;
	onActivateVehicle: (vehicle: FleetVehicle) => void;
	canWrite?: boolean;
};

export default function FleetDataTable({
	rows,
	selectedRows,
	setSelectedRows,
	selectionDisabled = false,
	onEditVehicle,
	onDuplicateVehicle,
	onDeactivateVehicle,
	onActivateVehicle,
	canWrite = false
}: FleetDataTableProps) {
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<keyof FleetVehicle>("registration");
	const [visibleColumns, setVisibleColumns] = useColumnVisibility(
		["personnelAndFleet", "fleet", "grid"],
		headCells as ColumnDef[]
	);

	const sortedRows = useMemo(
		() => [...rows].sort(getComparator(order, orderBy)),
		[rows, order, orderBy]
	);

	const selectedVisibleCount = useMemo(
		() => rows.filter((r) => selectedRows.has(r.id)).length,
		[rows, selectedRows]
	);

	const isAllSelected = rows.length > 0 && selectedVisibleCount === rows.length;
	const isIndeterminate = selectedVisibleCount > 0 && selectedVisibleCount < rows.length;

	const handleRequestSort = (property: keyof FleetVehicle) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (selectionDisabled) {
			return;
		}

		if (event.target.checked) {
			setSelectedRows((prev) => {
				const next = new Set(prev);
				rows.forEach((row) => next.add(row.id));
				return next;
			});
			return;
		}

		setSelectedRows((prev) => {
			const next = new Set(prev);
			rows.forEach((row) => next.delete(row.id));
			return next;
		});
	};

	const { handleRowClick } = useRangeSelection(sortedRows, selectedRows, setSelectedRows);

	return (
		<TableContainer
			sx={{
				flexGrow: 1,
				height: "100%",
				maxHeight: "100%",
				backgroundColor: "background.paper",
				overflow: "auto",
				borderRadius: 0,
				borderBottom: 1,
				borderTop: 1,
				borderColor: "border.hover"
			}}
		>
			<Table stickyHeader size="small">
				<TableHead>
					<TableRow>
						<TableCell
							sx={{
								width: `${checkboxColumnWidth}px`,
								minWidth: `${checkboxColumnWidth}px`,
								maxWidth: `${checkboxColumnWidth}px`,
								p: 0,
								textAlign: "center",
								borderRight: 1,
								borderColor: "border.hover",
								backgroundColor: "divider"
							}}
						>
							<Checkbox
								size="small"
								checked={isAllSelected}
								indeterminate={isIndeterminate}
								disabled={selectionDisabled}
								onChange={handleSelectAll}
							/>
						</TableCell>
						{headCells.filter((hc) => visibleColumns.has(hc.id)).map((headCell) => (
							<TableCell
								key={headCell.id}
								sortDirection={orderBy === headCell.id ? order : false}
								sx={{
									backgroundColor: "divider",
									borderRight: 1,
									borderColor: "border.hover",
									textWrap: "nowrap"
								}}
							>
								<TableSortLabel
									active={orderBy === headCell.id}
									direction={orderBy === headCell.id ? order : "asc"}
									onClick={() => handleRequestSort(headCell.id)}
									sx={{ "& .MuiTableSortLabel-icon": { color: "text.secondary" } }}
								>
									<Typography variant="text12Uppercase">{headCell.label}</Typography>
								</TableSortLabel>
							</TableCell>
						))}
						<TableCell
							sx={{
								width: `${actionColumnWidth}px`,
								minWidth: `${actionColumnWidth}px`,
								maxWidth: `${actionColumnWidth}px`,
								backgroundColor: "divider",
								borderBottom: 1,
								borderColor: "border.hover",
								p: 1
							}}
						>
							<ColumnVisibilityPopover
								columns={headCells as ColumnDef[]}
								visibleColumns={visibleColumns}
								onApply={setVisibleColumns}
							/>
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					{sortedRows.map((row, index) => {
						const isSelected = selectedRows.has(row.id);
						const isActive = row.status === FleetVehicleStatus.ACTIVE;
						const actionItems = getFleetVehicleActionItems(row, {
							onEditVehicle,
							onDuplicateVehicle,
							onDeactivateVehicle,
							onActivateVehicle
						});

						return (
							<TableRow
								key={row.id}
								selected={isSelected}
								hover
								sx={{ backgroundColor: "background.paper" }}
							>
								<TableCell sx={{ ...cellStyle, p: 0, textAlign: "center", width: `${checkboxColumnWidth}px`, minWidth: `${checkboxColumnWidth}px`, maxWidth: `${checkboxColumnWidth}px` }}>
									<Checkbox
										size="small"
										checked={isSelected}
										disabled={selectionDisabled}
										onChange={(e) =>
											handleRowClick(row.id, index, (e.nativeEvent as MouseEvent).shiftKey)
										}
									/>
								</TableCell>
								{visibleColumns.has("registration") && (
									<TableCell sx={cellStyle}>
										<Typography variant="text14">{row.registration || "-"}</Typography>
									</TableCell>
								)}
								{visibleColumns.has("type") && (
									<TableCell sx={cellStyle}>
										<Typography variant="text14">{row.type || "-"}</Typography>
									</TableCell>
								)}
								{visibleColumns.has("lengthM") && (
									<TableCell sx={cellStyle}>
										<Typography variant="text14">{row.lengthM ?? "-"}</Typography>
									</TableCell>
								)}
								{visibleColumns.has("refrigerated") && (
									<TableCell sx={cellStyle}>
										<Typography variant="text14">{getRefrigeratedLabel(row.refrigerated)}</Typography>
									</TableCell>
								)}
								{visibleColumns.has("defaultDriver") && (
									<TableCell sx={cellStyle}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<Typography
												variant="text14"
												color={row.driverActive === false ? "text.disabled" : "inherit"}
											>
												{row.defaultDriver || "-"}
											</Typography>
											{row.driverActive === false && (
												<Tooltip title="Default driver is deactivated">
													<WarningAmberIcon fontSize="small" color="warning" />
												</Tooltip>
											)}
										</Box>
									</TableCell>
								)}
								{visibleColumns.has("tractorRego") && (
									<TableCell sx={cellStyle}>
										<Typography variant="text14">{row.tractorRego || "-"}</Typography>
									</TableCell>
								)}
								{visibleColumns.has("trailer1Rego") && (
									<TableCell sx={cellStyle}>
										<Typography variant="text14">{row.trailer1Rego || "-"}</Typography>
									</TableCell>
								)}
								{visibleColumns.has("trailer2Rego") && (
									<TableCell sx={cellStyle}>
										<Typography variant="text14">{row.trailer2Rego || "-"}</Typography>
									</TableCell>
								)}
								{visibleColumns.has("status") && (
									<TableCell sx={cellStyle}>
										<StatusCell
											isActive={isActive}
											label={
												row.status
													? (FLEET_STATUS_LABELS[row.status as FleetVehicleStatus] ?? row.status)
													: "-"
											}
										/>
									</TableCell>
								)}
								<TableCell sx={{ ...cellStyle, borderRight: "none", p: 0.5, width: `${actionColumnWidth}px`, minWidth: `${actionColumnWidth}px`, maxWidth: `${actionColumnWidth}px` }}>
									{canWrite && <ActionCell items={actionItems} />}
								</TableCell>
							</TableRow>
						);
					})}

					{sortedRows.length === 0 && (
						<TableRow>
							<TableCell
								colSpan={visibleColumns.size + 2}
								sx={{ py: 4, textAlign: "center", color: "text.secondary" }}
							>
								No fleet vehicles found.
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
}
